'use client'

import { Fragment, useMemo } from 'react'
import { Restaurant, WithContext } from 'schema-dts'
import { useGetRestaurantQuery } from '@/api/restaurant/get-restaurant-query'
import { parseReadableRating } from '@/features/reviews/utils/reviews-helpers'
import { UrlConfig } from '@/setup/routing'
import { range } from '@/utils/functions/range'
import { createImageUrl } from '@/utils/image/create-image-url'

interface IRestaurantStructuredDataProps {
    citySlug: string
    restaurantSlug: string
}

export const RestaurantStructuredData = ({
    citySlug,
    restaurantSlug,
}: IRestaurantStructuredDataProps) => {
    // Prefetched and hydrated by the server component above, so this reads from cache.
    const { data } = useGetRestaurantQuery(restaurantSlug)

    const restaurant = data?.data

    const jsonLd = useMemo<WithContext<Restaurant> | undefined>(() => {
        if (!restaurant) return

        const firstImage = restaurant.visuals?.filter((visual) => visual.type === 'IMAGE')[0]

        return {
            '@context': 'https://schema.org',
            '@type': 'Restaurant',
            name: restaurant.name,
            telephone: restaurant.phone ?? undefined,

            address: restaurant.address
                ? {
                      '@type': 'PostalAddress',
                      streetAddress: restaurant.address ?? undefined,
                      addressLocality: restaurant.city?.name,
                      postalCode: restaurant.zipCode ?? undefined,
                      addressCountry: restaurant.city?.country?.shortCode ?? undefined,
                  }
                : undefined,

            url: UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug,
                restaurantSlug,
            }),

            image: firstImage?.visualUrl
                ? createImageUrl(firstImage.visualUrl, {
                      width: 1200,
                      aspectRatio: 16 / 9,
                      resolution: 1,
                  })
                : undefined,

            servesCuisine: restaurant.tags?.reduce(
                (acc, tag) => (acc ? `${acc}, ${tag.name}` : tag.name),
                ''
            ),

            aggregateRating:
                typeof restaurant.avgRating === 'number' && Boolean(restaurant.ratingsCount)
                    ? {
                          '@type': 'AggregateRating',
                          ratingValue: parseReadableRating(
                              restaurant.avgRating,
                              restaurant.ratingsCount
                          ),
                          reviewCount: restaurant.ratingsCount,
                      }
                    : undefined,

            priceRange: restaurant.priceRange
                ? range(restaurant.priceRange).reduce(
                      (acc) => `${acc}${restaurant.city?.country?.currency?.symbol ?? '$'}`,
                      ''
                  )
                : undefined,

            review: restaurant.reviews?.map((review) => {
                return {
                    '@type': 'Review',
                    author: {
                        '@type': 'Person',
                        name: review.name ?? undefined,
                    },
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: review.rating,
                        bestRating: 5,
                        worstRating: 1,
                    },
                    datePublished: review.dateTimestamp ? String(review.dateTimestamp) : undefined,
                    description: review.review ?? undefined,
                }
            }),
        }
    }, [restaurant, citySlug, restaurantSlug])

    return (
        <Fragment>
            {jsonLd && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(jsonLd),
                    }}
                />
            )}
        </Fragment>
    )
}
